import { SEO } from '@/components/SEO';
import { Shield, Lock, Eye, FileCheck, Server, Users } from 'lucide-react';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export function SecurityPage() {
  const securityFeatures = [
    {
      icon: Lock,
      title: 'Data Encryption',
      description: 'All data is encrypted in transit (TLS 1.3) and at rest (AES-256)'
    },
    {
      icon: Shield,
      title: 'SOC 2 Type II',
      description: 'Independently audited and certified for security controls'
    },
    {
      icon: Eye,
      title: 'Access Controls',
      description: 'Role-based access control (RBAC) with multi-factor authentication'
    },
    {
      icon: FileCheck,
      title: 'Audit Logs',
      description: 'Comprehensive logging of all system activities and data access'
    },
    {
      icon: Server,
      title: 'Infrastructure',
      description: 'Hosted on enterprise-grade cloud infrastructure with 99.9% uptime SLA'
    },
    {
      icon: Users,
      title: 'Regular Testing',
      description: 'Quarterly penetration testing and vulnerability assessments'
    },
  ];

  return (
    <>
      <SEO
        title="Security - Nestor"
        description="Learn about our enterprise-grade security practices, certifications, and compliance standards."
      />

      <div className="container mx-auto px-4 py-20 max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Security & Compliance</h1>
          <p className="text-xl text-muted-foreground">
            Your data security is our highest priority
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {securityFeatures.map((feature, index) => (
            <Card key={index}>
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>{feature.title}</CardTitle>
                <CardDescription>{feature.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>

        <div className="prose prose-lg max-w-none space-y-6 text-muted-foreground">
          <section>
            <h2 className="text-2xl font-semibold text-foreground">Certifications & Compliance</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>ISO 27001 Certified</li>
              <li>SOC 2 Type II Compliant</li>
              <li>GDPR Ready</li>
              <li>India DPDP Act Compliant</li>
              <li>HIPAA Compliant (Healthcare module)</li>
              <li>PCI DSS Compliant (Payment processing)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground">Data Protection</h2>
            <p>
              We implement multiple layers of security to protect your data:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular automated backups with point-in-time recovery</li>
              <li>Geographic redundancy across multiple data centers</li>
              <li>DDoS protection and web application firewall</li>
              <li>Intrusion detection and prevention systems</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground">Security Team</h2>
            <p>
              Our dedicated security team monitors our systems 24/7 and responds to any incidents immediately.
              We conduct regular security training for all employees and maintain an incident response plan.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground">Reporting Security Issues</h2>
            <p>
              If you discover a security vulnerability, please report it to security@hrms.com.
              We take all reports seriously and will respond promptly.
            </p>
          </section>
        </div>
      </div>
    </>
  );
}
